import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@/translations';

import {
  Alert,
  App,
  AutoComplete,
  Button,
  Input,
  InputNumber,
  Select,
  Spin,
  Table,
  TreeSelect,
} from 'antd';
import {
  faAdd,
  faEdit,
  faMinus,
  faTrash,
  faXmark,
  Icon,
} from '@/ui/components';
import {
  useAddInspectionsVehicleDamageParts,
  useDeleteInspectionsVehicleDamagePart,
  useInspectionsSearchParts,
} from '@/api/Inspections';
import { Damage, DamagePart, DamagePartData, Part } from '@/types';
import { ColumnsType } from 'antd/es/table';
import { currencyFormatted } from '@/utils/helpers';
import { StyledLink } from '@/ui/globalStyles';
import { useAppCustomers } from '@/hooks';
import { getDamageActionTreeData } from './DamageActionOptions';
import {
  buildSearchKey,
  getCachedOrFetchList,
  getNextRowRequestId,
  isLatestRowRequest,
} from '../searchRequestUtils';
import { useRefreshDamageLines } from '../useRefreshDamageLines';
import {
  isExistingRowEditing,
  patchEditingRow,
  removeEditingRow,
  upsertEditingRow,
  withEditingRows,
} from '../inlineRowEdit';
import { isDropdownOpen } from '../inlineCellNavigation';
import { InlineNavCell } from '../InlineNavCell';
import { useInlineCellNavigation } from '../useInlineCellNavigation';
import {
  addPartsShortcutHint,
  isAddPartsShortcut,
  isSaveNotesShortcut,
  saveNotesShortcutHint,
} from '../preTimingShortcuts';
import { useInlineAddShortcut } from '../useInlineAddShortcut';

interface DamagePartsGridProps {
  title?: string;
  disabled?: boolean;
  canDeleteExisting?: boolean;
  inspectionId: number;
  damageActionId?: number;
  damagePartsData?: DamagePart[];
  damageLines?: Damage[];
  damageLinesLoading?: boolean;
  onSubmit: () => void | Promise<unknown>;
}
export const DamagePartsGrid: FC<DamagePartsGridProps> = ({
  title,
  disabled,
  canDeleteExisting,
  inspectionId,
  damageActionId,
  damagePartsData,
  damageLines,
  damageLinesLoading,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const { hasDamageApproveAccess } = useAppCustomers();
  const tableRef = useRef<HTMLDivElement>(null);
  useInlineCellNavigation(tableRef, !disabled);

  const { mutateAsync: fetchParts, isPending: partsLoading } =
    useInspectionsSearchParts();
  const partSearchSeqByRowRef = useRef<Record<number, number>>({});
  const partSearchCacheRef = useRef(new Map<string, Part[]>());
  const partSearchInFlightRef = useRef(
    new Map<string, Promise<{ data?: Part[] }>>(),
  );

  const { mutateAsync: addDamagePartsAsync, isPending: addDamagePartsLoading } =
    useAddInspectionsVehicleDamageParts();

  const { mutateAsync: deleteDamagePartAsync } =
    useDeleteInspectionsVehicleDamagePart();

  const [partsMap, setPartsMap] = useState<Record<string, Part[]>>({});
  const setParts = (id: number, options: Part[]) => {
    setPartsMap((prev) => ({
      ...prev,
      [id]: options,
    }));
  };

  const [editingRows, setEditingRows] = useState<DamagePartData[]>([]);

  const [damageParts, setDamageParts] = useState<DamagePartData[]>([]);
  const { snapshot, refreshing, refreshDamageLines } =
    useRefreshDamageLines<DamagePartData>();
  const list: DamagePartData[] = (
    damagePartsData
      ? damagePartsData.map(
          (o, idx) =>
            ({
              id: idx,
              partId: o.id,
              type: o.type,
              referenceId: o.referenceId,
              partNumber: o.partNumber,
              description: o.description,
              quantity: o.quantity,
              price: hasDamageApproveAccess ? o.retailCost : o.cost,
              lineCost: hasDamageApproveAccess ? o.retailLineCost : o.lineCost,
              damageActionIds: damageActionId ? [damageActionId] : [],
            }) as DamagePartData,
        )
      : []
  ).concat(damageParts);
  const [error, setError] = useState<string>();

  const getDefaultDamagePart = useCallback(
    (id: number) =>
      ({
        id: id,
        type: '',
        referenceId: 0,
        partNumber: '',
        description: '',
        quantity: 1,
        price: 0,
        lineCost: 0,
        damageActionIds: damageActionId ? [damageActionId] : [],
      }) as DamagePartData,
    [damageActionId],
  );

  const resetRecords = useCallback(() => {
    setError(undefined);
    setPartsMap({});
    setDamageParts(damageActionId ? [] : [getDefaultDamagePart(0)]);
    setEditingRows([]);
  }, [damageActionId, getDefaultDamagePart]);

  useEffect(() => {
    resetRecords();
  }, [resetRecords]);

  const onAdd = () => {
    const copyList = [...damageParts];
    const nextId =
      copyList.length > 0
        ? Math.max(...copyList.map((o) => o.id)) + 1
        : damagePartsData?.length || 0;
    copyList.push(getDefaultDamagePart(nextId));
    setDamageParts(copyList);
  };
  const partsAddRef = useInlineAddShortcut(
    'parts',
    isAddPartsShortcut,
    onAdd,
    !disabled,
  );

  const onDelete = (id: number, exist: boolean) => {
    if (exist) {
      modal.confirm({
        title: 'Confirm',
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: async () => {
          await deleteDamagePartAsync({
            partNumberId: id,
            inspectionId: inspectionId,
          });
          onSubmit();
        },
        onCancel: () => {},
      });
    } else {
      const copyList = [...damageParts].filter((o) => o.id !== id);
      setDamageParts(copyList);
    }
  };

  const handleSearch = async (id: number, partNumber: string, type: string) => {
    setParts(id, []);
    if (!partNumber) return;

    const normalizedPartNumber = partNumber.trim();
    const normalizedType = (type || '').trim();
    const searchKey = buildSearchKey(
      inspectionId,
      normalizedType,
      normalizedPartNumber,
    );
    const requestId = getNextRowRequestId(partSearchSeqByRowRef, id);

    const parts = await getCachedOrFetchList({
      key: searchKey,
      cacheRef: partSearchCacheRef,
      inFlightRef: partSearchInFlightRef,
      fetcher: () =>
        fetchParts({
          inspectionId,
          partNumber: normalizedPartNumber,
          type: normalizedType,
        }),
    });
    if (!isLatestRowRequest(partSearchSeqByRowRef, id, requestId)) return;

    setParts(id, parts);
    const part = parts.find(
      (o) => o.partNumber?.toLowerCase() === normalizedPartNumber.toLowerCase(),
    );
    if (part) {
      onChangePartNumber(id, part);
    }
  };

  const onChangeValue = (
    id: number,
    field: keyof DamagePartData,
    value: any,
  ) => {
    setError(undefined);
    if (editingRows.some((row) => row.id === id && row.partId)) {
      setEditingRows((current) =>
        patchEditingRow(current, id, { [field]: value }),
      );
      return;
    }
    const copyList = [...damageParts];
    const item: any = copyList.find((o) => o.id === id);
    if (item) {
      item[field] = value;
      setDamageParts(copyList);
    }
  };

  const onChangePartNumber = (id: number, selectedOption: Part) => {
    if (editingRows.some((row) => row.id === id && row.partId)) {
      setEditingRows((current) =>
        current.map((row) =>
          row.id === id && row.partId
            ? {
                ...row,
                referenceId: selectedOption.id,
                partNumber: selectedOption.partNumber,
                description: selectedOption.description,
                quantity: 1,
                price: selectedOption.price,
                lineCost: selectedOption.price,
              }
            : row,
        ),
      );
      return;
    }
    const copyList = [...damageParts];
    const item = copyList.find((o) => o.id === id);
    if (item) {
      item.referenceId = selectedOption.id;
      item.partNumber = selectedOption.partNumber;
      item.description = selectedOption.description;
      item.quantity = 1;
      item.price = selectedOption.price;
      item.lineCost = selectedOption.price;
      setDamageParts(copyList);
    }
  };

  const onSave = async () => {
    if (
      damageParts.some((o) => o.type.length === 0) ||
      editingRows.some((o) => !o.type)
    ) {
      setError('Some of the type not choosed');
      return;
    }
    if (
      damageParts.some((o) => o.referenceId === 0) ||
      editingRows.some((o) => !o.referenceId)
    ) {
      setError('Some of the part number not choosed');
      return;
    }
    if (
      damageParts.some((o) => o.quantity < 0) ||
      editingRows.some((o) => o.quantity < 0)
    ) {
      setError('The quantity must be a non-negative number');
      return;
    }
    if (
      damageParts.some((o) => o.price < 0) ||
      editingRows.some((o) => o.price < 0)
    ) {
      setError('The cost must be a non-negative number');
      return;
    }
    if (damageParts.some((o) => o.damageActionIds.length == 0)) {
      setError('Please choose the flat codes');
      return;
    }

    const res = await addDamagePartsAsync({
      inspectionId,
      parts: [
        ...editingRows.map((row) => ({
          id: row.partId,
          type: row.type,
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageActionIds: row.damageActionIds,
        })),
        ...damageParts.map((row) => ({
          type: row.type,
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageActionIds: row.damageActionIds,
        })),
      ],
    });
    if (!res.success) {
      setError(res.message.Message);
      return;
    }

    const drafts = editingRows.map((row) => ({
      ...row,
      lineCost: (row.price || 0) * (row.quantity || 0),
    }));
    await refreshDamageLines(
      withEditingRows(list, drafts, 'partId'),
      onSubmit,
      resetRecords,
    );
  };

  const readOnly = (record: DamagePartData) =>
    !!disabled ||
    (!!record.partId && !isExistingRowEditing(record, editingRows, 'partId'));
  const tableRows = withEditingRows(
    snapshot ?? list,
    editingRows.map((row) => ({
      ...row,
      lineCost: (row.price || 0) * (row.quantity || 0),
    })),
    'partId',
  );

  const columns: ColumnsType<DamagePartData> = [
    {
      title: 'Type',
      width: 120,
      render: (_, record) =>
        readOnly(record) ? (
          record.type
        ) : (
          <InlineNavCell row={record.id} col="type">
            <Select
              className="w-100"
              value={record.type}
              onChange={(e) => onChangeValue(record.id, 'type', e)}
              options={['Part', 'Fluid', 'Paint']?.map((item) => ({
                label: item,
                value: item,
              }))}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('code'),
      width: 200,
      render: (_, record) =>
        readOnly(record) ? (
          record.partNumber
        ) : (
          <InlineNavCell row={record.id} col="code">
            <AutoComplete
              className="w-100"
              value={record.partNumber}
              options={(partsMap[record.id] || []).map((o) => ({
                value: o.partNumber,
                label: o.partNumber,
              }))}
              onSearch={(e) => {
                onChangeValue(record.id, 'partNumber', e);
                handleSearch(record.id, e, record.type);
              }}
              onSelect={(_, option) => {
                const options = partsMap[record.id] || [];
                const selectedOption = options.find(
                  (o) => o.partNumber === option.value,
                );
                if (selectedOption) {
                  onChangePartNumber(record.id, selectedOption);
                }
              }}
              notFoundContent={
                partsLoading ? <Spin size="small" /> : 'No results'
              }
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('description'),
      width: 200,
      render: (_, record) =>
        readOnly(record) ? (
          record.description
        ) : (
          <InlineNavCell row={record.id} col="notes">
            <Input.TextArea
              rows={1}
              value={record.description}
              onChange={(e) =>
                onChangeValue(record.id, 'description', e.target.value)
              }
              placeholder={t('shortcutSaveNotes', {
                shortcut: saveNotesShortcutHint(),
              })}
              onKeyDown={(event) => {
                if (!isSaveNotesShortcut(event.nativeEvent)) return;
                event.preventDefault();
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('quantity'),
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          record.quantity
        ) : (
          <InlineNavCell row={record.id} col="quantity">
            <InputNumber
              value={record.quantity}
              onChange={(e) => onChangeValue(record.id, 'quantity', e || 1)}
              onPressEnter={() => {
                if (isDropdownOpen()) return;
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('cost'),
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          currencyFormatted(record.price)
        ) : (
          <InlineNavCell row={record.id} col="cost">
            <InputNumber
              prefix="£"
              value={record.price}
              onChange={(e) => onChangeValue(record.id, 'price', e || 0)}
              onPressEnter={() => {
                if (isDropdownOpen()) return;
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('lineCost'),
      width: 100,
      render: (_, record) => currencyFormatted(record.lineCost),
    },
    ...(damageLines
      ? [
          {
            title: t('frCodes'),
            width: 300,
            render: (_: any, record: DamagePartData) => (
              <TreeSelect
                className="w-100"
                showSearch
                multiple
                allowClear
                treeCheckable
                showCheckedStrategy="SHOW_CHILD"
                treeData={getDamageActionTreeData(
                  damageLines || [],
                  record.partNumber,
                )}
                value={record.damageActionIds}
                onChange={(value) => {
                  onChangeValue(record.id, 'damageActionIds', value || []);
                }}
              />
            ),
          },
        ]
      : []),
    ...(disabled && !canDeleteExisting
      ? []
      : [
          {
            dataIndex: 'id',
            width: 88,
            render: (_: any, record: DamagePartData) => (
              <div className="d-flex justify-content-center gap-3">
                {disabled ? (
                  record.partId ? (
                    <StyledLink
                      onClick={() => onDelete(record.partId || 0, true)}
                    >
                      <Icon icon={faTrash} />
                    </StyledLink>
                  ) : null
                ) : record.partId ? (
                  isExistingRowEditing(record, editingRows, 'partId') ? (
                    <StyledLink
                      onClick={() => {
                        setError(undefined);
                        setEditingRows((current) =>
                          removeEditingRow(current, record.partId, 'partId'),
                        );
                      }}
                    >
                      <Icon icon={faXmark} />
                    </StyledLink>
                  ) : (
                    <>
                      <StyledLink
                        onClick={() =>
                          setEditingRows((current) =>
                            upsertEditingRow(current, record, 'partId'),
                          )
                        }
                      >
                        <Icon icon={faEdit} />
                      </StyledLink>
                      <StyledLink
                        onClick={() => onDelete(record.partId || 0, true)}
                      >
                        <Icon icon={faTrash} />
                      </StyledLink>
                    </>
                  )
                ) : (
                  <Button
                    size="small"
                    shape="circle"
                    color="danger"
                    variant="outlined"
                    onClick={() => onDelete(record.id, false)}
                  >
                    <Icon icon={faMinus} />
                  </Button>
                )}
              </div>
            ),
          },
        ]),
  ];

  return (
    <div ref={partsAddRef} data-inline-add="parts">
      <Spin
        spinning={addDamagePartsLoading || refreshing || !!damageLinesLoading}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="m-0 text-uppercase">
            {title} ({list.length})
          </h6>
          {!disabled ? (
            <div>
              <Button
                shape="circle"
                className="mx-3"
                title={addPartsShortcutHint()}
                onClick={onAdd}
              >
                <Icon icon={faAdd} />
              </Button>
              <Button
                type="primary"
                title={saveNotesShortcutHint()}
                disabled={
                  addDamagePartsLoading ||
                  (damageParts.length === 0 && editingRows.length === 0)
                }
                onClick={onSave}
              >
                {t('save')}
              </Button>
            </div>
          ) : null}
        </div>

        <div ref={tableRef}>
          <Table
            className="m-0"
            columns={columns}
            dataSource={tableRows}
            rowKey="id"
            pagination={false}
          />
        </div>

        {error ? <Alert message={error} type="error" className="mt-3" /> : null}
      </Spin>
    </div>
  );
};
